/**
 * DriverVehicleCard - Current vehicle info for driver
 */
import React, { useState, useEffect } from 'react';
import { Car, Shield, Wrench, Gauge, AlertCircle, Calendar } from 'lucide-react';
import { driverMonoAPI } from '../../../api/missions.js';
import { useFormat } from '../../../i18n/format.js';

export default function DriverVehicleCard() {
  const { fmt } = useFormat();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadVehicle();
  }, []);

  const loadVehicle = async () => {
    try {
      setLoading(true);
      const res = await driverMonoAPI.getVehicle();
      setData(res.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="h-5 w-5 text-red-500" />
        <p className="text-red-700 dark:text-red-400">{error}</p>
      </div>
    );
  }

  if (!data || !data.vehicle) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-8 text-center">
        <Car className="h-12 w-12 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
        <p className="text-gray-500 dark:text-slate-400">Aucun véhicule actuellement assigné</p>
      </div>
    );
  }

  const { vehicle, insurance, next_maintenance } = data;

  return (
    <div className="space-y-4">
      {/* Vehicle Main Card */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-5">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 bg-blue-50 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
            <Car className="h-7 w-7 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              {vehicle.make} {vehicle.model}
            </h3>
            <p className="text-lg font-mono text-gray-600 dark:text-slate-400">{vehicle.registration_plate}</p>
          </div>
          <span className={`ml-auto px-3 py-1 rounded-full text-xs font-medium ${
            vehicle.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
            vehicle.status === 'maintenance' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
            'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400'
          }`}>
            {vehicle.status === 'active' ? 'Actif' :
             vehicle.status === 'maintenance' ? 'Maintenance' :
             vehicle.status}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {vehicle.year && (
            <div>
              <p className="text-xs text-gray-500 dark:text-slate-400">Année</p>
              <p className="font-medium text-gray-900 dark:text-white">{vehicle.year}</p>
            </div>
          )}
          {vehicle.fuel_type && (
            <div>
              <p className="text-xs text-gray-500 dark:text-slate-400">Carburant</p>
              <p className="font-medium text-gray-900 dark:text-white capitalize">{vehicle.fuel_type}</p>
            </div>
          )}
          {vehicle.vin && (
            <div>
              <p className="text-xs text-gray-500 dark:text-slate-400">VIN</p>
              <p className="font-mono text-sm text-gray-900 dark:text-slate-300">{vehicle.vin}</p>
            </div>
          )}
          {vehicle.mileage && (
            <div className="flex items-center gap-2">
              <Gauge className="h-4 w-4 text-gray-400 dark:text-slate-500" />
              <div>
                <p className="text-xs text-gray-500 dark:text-slate-400">Kilométrage</p>
                <p className="font-medium text-gray-900 dark:text-white">{fmt.number(parseInt(vehicle.mileage))} km</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Insurance Card */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Shield className="h-5 w-5 text-green-600 dark:text-green-400" />
          <h4 className="font-semibold text-gray-900 dark:text-white">Assurance</h4>
        </div>
        {insurance ? (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500 dark:text-slate-400">Contrat</p>
              <p className="font-medium text-gray-900 dark:text-white">{insurance.contract_number || insurance.policy_number || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-slate-400">Statut</p>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                insurance.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400'
              }`}>
                {insurance.status === 'active' ? 'Actif' : insurance.status}
              </span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-400 dark:text-slate-500">Aucune assurance liée</p>
        )}
      </div>

      {/* Next Maintenance Card */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Wrench className="h-5 w-5 text-orange-600 dark:text-orange-400" />
          <h4 className="font-semibold text-gray-900 dark:text-white">Prochaine Maintenance</h4>
        </div>
        {next_maintenance ? (
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{next_maintenance.title || next_maintenance.description || 'Maintenance planifiée'}</p>
            {next_maintenance.scheduled_date && (
              <p className="text-sm text-gray-500 dark:text-slate-400 mt-1 flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                {fmt.date(next_maintenance.scheduled_date, {
                  day: '2-digit', month: 'long', year: 'numeric'
                })}
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-400 dark:text-slate-500">Aucune maintenance planifiée</p>
        )}
      </div>
    </div>
  );
}
